import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { loadStripe } from '@stripe/stripe-js'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { showToast } from 'shared/ui'
import { logger } from 'shared/lib'
import { STRIPE_KEY } from 'shared/config'
import { BillingApi } from 'entities/Billing/api/billing'
import { AddBankAccountContent } from '../ui/AddBankAccountContent'

const stripePromise = STRIPE_KEY ? loadStripe(STRIPE_KEY) : null

export class AddBankAccountStore {
  private _modalId = 'add_bank_account_id'
  private _clientSecret: string | null = null
  private _loading = false
  private _error: string | null = null
  private _disposeReaction?: () => void
  private _onConfirmSetup: (() => Promise<{ error?: { message?: string } | null }>) | null = null

  constructor(private _onSuccess: () => void) {
    makeAutoObservable(this)
    this._init()
  }

  private _init = async () => {
    runInAction(() => {
      this._loading = true
    })
    try {
      const { data } = await BillingApi.createSetupIntent()
      runInAction(() => {
        this._clientSecret = data.client_secret
      })
      this._openModal()
    } catch (e) {
      logger.error(e)
      showToast({
        type: 'error',
        title: 'Failed to initialize bank account setup. Please try again.',
      })
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  private _openModal = () => {
    modalStore.addModal({
      id: this._modalId,
      title: 'Add bank account',
      width: 480,
      ModalContent: AddBankAccountContent,
      ModalContentProps: { store: this },
      primaryAction: {
        text: 'Connect bank account',
        onAction: this._onConfirm,
        disabled: false,
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: this._closeModal,
      },
    })

    this._disposeReaction?.()
    this._disposeReaction = reaction(
      () => this._loading,
      (isLoading) => {
        const modal = modalStore.getModal(this._modalId)
        if (modal && 'primaryAction' in modal && modal.primaryAction) {
          modal.primaryAction.disabled = isLoading
        }
      }
    )
  }

  private _onConfirm = async () => {
    if (this._loading || !this._onConfirmSetup) return

    runInAction(() => {
      this._loading = true
      this._error = null
    })

    const { error } = await this._onConfirmSetup()

    if (error) {
      runInAction(() => {
        this._error = error.message || 'Something went wrong. Please try again.'
        this._loading = false
      })
      return
    }

    runInAction(() => {
      this._loading = false
    })
    this._closeModal()
    showToast({ type: 'success', title: 'ACH added' })
    this._onSuccess()
  }

  setOnConfirmSetup = (fn: () => Promise<{ error?: { message?: string } | null }>) => {
    this._onConfirmSetup = fn
  }

  private _closeModal = () => {
    this._disposeReaction?.()
    modalStore.removeModal(this._modalId)
  }

  get stripePromise() {
    return stripePromise
  }

  get clientSecret() {
    return this._clientSecret
  }

  get loading() {
    return this._loading
  }

  get error() {
    return this._error
  }
}
